import React, { useState } from 'react';
import { RiPushpinLine, RiUnpinLine } from '@remixicon/react';
import CardShell from '../../../components/CardShell';
import { MOIS_12, sumNN, realOffre } from './model';
import '../OfferMonthPage.css';

/* L'ATTERRISSAGE EN CA (proto `WIP/protos/ventes-atterrissage.html`, validé le 2026-10-11 ; gardé dans l'écran A3
   à la demande d'Olivier, 2026-10-22) : le CA réalisé, plus ce que le portefeuille et les nouvelles ventes donnent
   d'ici décembre, face à la cible ; puis chaque offre. Il suit ce qu'on tape, sans rien conseiller ni filtrer.
   Une punaise le garde dans sa colonne ou le libère : libéré, la grille prend toute la largeur, une languette
   garde l'écart en vue, et le panneau revient en flottant au survol du bord droit. */

const MOIS_LONGS = (p) => [p('janvier'), p('février'), p('mars'), p('avril'), p('mai'), p('juin'),
                           p('juillet'), p('août'), p('septembre'), p('octobre'), p('novembre'), p('décembre')];

export default function AtterrissagePanel({ c }) {
  const { p, fmt, vue, realise, year, libre, setLibre } = c;
  const [vu, setVu] = useState(false);
  const NA = <span className="om-na">{p('n/a')}</span>;
  const ouverts = vue.open.filter((i) => i < 12);
  const clos = MOIS_12.filter((i) => !ouverts.includes(i));
  const finClos = clos.length ? MOIS_LONGS(p)[ouverts.length ? ouverts[0] - 1 : 11] : null;
  const seuil = c.unit === 'kchf' ? 500 : 0.5;
  const ecart = (v) => (v == null ? NA
    : <span className={v > 0 ? 'om-pos' : v < 0 ? 'om-neg' : ''}>{Math.abs(v) < seuil ? '–' : `${v > 0 ? '+' : ''}${fmt.val(v)}`}</span>);
  const nomCible = (() => {
    const t = vue.target;
    if (!t || !t.version_id) return p('Trajectoire');
    return `${t.kind === 'trajectory' ? p('Trajectoire') : p('Prévisions')} · ${t.label}`;
  })();

  const vs = vue.rows.map((r) => {
    const reel = clos.length ? (sumNN(realOffre(realise, r).ca, clos) || 0) : 0;
    const bk = sumNN(r.parts.bk.ca, ouverts) || 0;
    const neuf = sumNN(r.parts.new.ca, ouverts) || 0;
    return { r, reel, bk, neuf, att: reel + bk + neuf, cible: r.target ? r.target.value : null };
  });
  const t = (k) => vs.reduce((a, x) => a + x[k], 0);
  const cible = vs.some((x) => x.cible == null) ? null : t('cible');
  const att = t('att');
  const echelle = Math.max(att, cible || 0) || 1;
  const part = (v) => `${Math.max(0, (v / echelle) * 100)}%`;
  const punaise = (
    <button type="button" className={`om-punaise${libre ? '' : ' is-on'}`}
            title={libre ? p("Épingler l'atterrissage") : p("Libérer l'atterrissage")}
            aria-label={libre ? p("Épingler l'atterrissage") : p("Libérer l'atterrissage")}
            aria-pressed={!libre}
            onClick={() => { setLibre(!libre); setVu(false); }}>
      {libre ? <RiUnpinLine size={15} aria-hidden="true" /> : <RiPushpinLine size={15} aria-hidden="true" />}
    </button>
  );
  const carte = (
    <CardShell title={`${p('Atterrissage')} · ${p('CA')} ${year}`}
               className={`om-panneau${libre ? ` om-panneau--flottant${vu ? '' : ' is-cache'}` : ''}`}
               primaryAction={punaise}>
      <div className="om-eq">
        <div className="om-eq__r"><span>{finClos ? `${p('CA réalisé à fin')} ${finClos}` : p('CA réalisé')}</span><span>{fmt.val(t('reel') || null)}</span></div>
        <div className="om-eq__r"><span>+ {p('CA du portefeuille')}</span><span>{fmt.val(t('bk') || null)}</span></div>
        <div className="om-eq__r"><span>+ {p('CA des nouvelles ventes')}</span><span>{fmt.val(t('neuf') || null)}</span></div>
        <div className="om-eq__r om-eq__r--fort"><span>= {p('Atterrissage')}</span><span>{fmt.val(att || null)}</span></div>
        <div className="om-eq__r om-eq__r--cible"><span>{p('Cible')} · {nomCible}</span><span>{cible == null ? NA : fmt.val(cible || null)}</span></div>
        <div className="om-eq__r"><span>{p('Écart à la cible')}</span><b>{cible == null ? NA : ecart(att - cible)}</b></div>
      </div>
      <div className="om-eq__barre" aria-hidden="true">
        <i className="om-eq__b--reel" style={{ width: part(t('reel')) }} />
        <i className="om-eq__b--bk" style={{ width: part(t('bk')) }} />
        <i className="om-eq__b--nv" style={{ width: part(t('neuf')) }} />
      </div>
      <table className="om-eq__offres">
        <thead><tr><th className="om-lbl">{p('Offre')}</th><th>{p('Atterrissage')}</th><th>{p('Cible')}</th><th>{p('Écart')}</th></tr></thead>
        <tbody>
          {vs.map((x) => (
            <tr key={x.r.offer_id}>
              <td className="om-lbl">{x.r.name}</td><td>{fmt.val(x.att || null)}</td>
              <td>{x.cible == null ? NA : fmt.val(x.cible || null)}</td><td>{x.cible == null ? NA : ecart(x.att - x.cible)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardShell>
  );
  if (!libre) return carte;
  return (
    <div className="om-flottant" onMouseLeave={() => setVu(false)}>
      <button type="button" className="om-languette" title={p("Ouvrir l'atterrissage")}
              onMouseEnter={() => setVu(true)} onClick={() => setVu(!vu)}>
        <span className="om-languette__t">{p('Atterrissage')}</span>
        {cible != null && <span className="om-languette__e">{ecart(att - cible)}</span>}
      </button>
      {carte}
    </div>
  );
}
